import {useEffect, useRef, useState} from "react";
import {DefaultValues, Path, useForm, UseFormReturn} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {number, object, ObjectSchema, Schema, string} from "yup";

import {IPhoneFieldValue} from "@pg-design/phone-input-module";

import {validationMessages} from "../../form/utils/validation_messages";
import {hitGTMEcommerceAddToCart} from "../../offer/tracking/ecommerce_events/hit_gtm_ecommerce_add_to_cart";
import {hitGTMEcommerceBeginCheckout} from "../../offer/tracking/ecommerce_events/hit_gtm_ecommerce_begin_checkout";
import {IOfferDetailApplication} from "../../offer/types/IOfferDetailApplication";
import {IPropertyDetailApplication} from "../../property/types/IPropertyDetailApplication";
import {Country} from "../../region/types/Country";
import {useAppSelector} from "../../store/hooks";
import {gtmCheckout} from "../../tracking/google_tag_manager/gtm_checkout";
import {gtmEventFormInquiry} from "../../tracking/google_tag_manager/gtm_event_form_inquiry";
import {IGTMEventLeadForm} from "../../tracking/google_tag_manager/gtm_event_form_open";
import {gtmProductAdd} from "../../tracking/google_tag_manager/gtm_product_add";
import {useHomeMatchPreferencesStorage} from "../../user/hooks/preferences/use_home_match_preferences_storage";
import {getPreferencesFromLeadData} from "../../user/utils/preferences/get_preferences_from_lead_data";
import {isValidationError} from "../../utils/isValidationError";
import {IApplicationFormValues} from "../types/form/IApplicationFormValues";
import {IApplicationData} from "../types/IApplicationData";
import {IApplicationReducerData} from "../types/IApplicationReducerData";
import {getRodoDataFromApplication} from "../utils/lead/get_rodo_data_from_application";
import {useLeadData} from "./use_lead_data";
import {useLeadRequiredParams} from "./use_lead_required_params";
import {useSendMainLead} from "./use_send_main_lead";

const baseFormSchema = object().shape({
    email: string().email().required(validationMessages.email),
    name: string().required(validationMessages.required),
    phone: object().shape({
        code: number().required(validationMessages.required),
        number: string().required(validationMessages.required)
    })
});

export interface IBaseDefaultValues {
    name: string;
    email: string;
    phone: IPhoneFieldValue;
    marketing_email: boolean;
    marketing_sms: boolean;
}

export interface IUseLeadFormConfig<TExtraValues extends Record<string, unknown> = Record<string, never>> {
    initialLeadFormValues: IApplicationFormValues | null;
    onSuccessfulSubmit: (
        sentApplication: IApplicationData | null,
        offer?: IOfferDetailApplication,
        property?: IPropertyDetailApplication,
        recommendedFilters?: Record<string, string>
    ) => Promise<void>;
    handleModalClose: (forceClose?: boolean) => void;
    extraDefaultValues?: TExtraValues;
    extraSchema?: ObjectSchema<Record<string, unknown>> | Record<string, unknown>;
    customSourceSection?: string;
    transformFormValues?: (values: IBaseDefaultValues & TExtraValues, defaultValues: IBaseDefaultValues & TExtraValues) => IApplicationFormValues;
    onBeforeSubmit?: (values: IBaseDefaultValues & TExtraValues) => void;
    resetOnChange?: unknown[];
    analyticsParams?: {customSourceSection?: string};
}

export interface IUseLeadFormReturn<TExtraValues extends Record<string, unknown> = Record<string, never>> {
    formMethods: UseFormReturn<IBaseDefaultValues & TExtraValues>;
    isSending: boolean;
    onSubmit: (values: IBaseDefaultValues & TExtraValues) => Promise<void>;
    onInteraction: (formFillAction?: "autofill" | "manual_fill") => void;
    applicationData: IApplicationReducerData | undefined;
    vendorName: string | undefined;
    offerPersonalDataProcessorName: string | undefined;
    rodoLinks: ReturnType<typeof getRodoDataFromApplication>["rodoLinks"];
}

export function useLeadForm<TExtraValues extends Record<string, unknown> = Record<string, never>>(
    config: IUseLeadFormConfig<TExtraValues>
): IUseLeadFormReturn<TExtraValues> {
    const {
        initialLeadFormValues,
        onSuccessfulSubmit,
        handleModalClose,
        extraDefaultValues,
        extraSchema,
        customSourceSection,
        transformFormValues,
        onBeforeSubmit,
        resetOnChange,
        analyticsParams
    } = config;

    const [isSending, setIsSending] = useState(false);
    const wasInteractionHitRef = useRef(false);

    const {vendorId, offerId, propertyId} = useLeadRequiredParams();
    const leadModalState = useAppSelector((state) => state.mainLead);
    const applicationData = useLeadData({vendorId, offerId: offerId || undefined, propertyId: propertyId || undefined}) ?? undefined;
    const viewType = useAppSelector((state) => state.viewType.current);
    const mainLeadType = useAppSelector((state) => state.mainLead.mainLeadType);
    const {vendorName, offerPersonalDataProcessorName, rodoLinks} = getRodoDataFromApplication(applicationData);

    const baseDefaultValues: IBaseDefaultValues = {
        name: initialLeadFormValues?.name || "",
        email: initialLeadFormValues?.email || "",
        phone: initialLeadFormValues?.phone || {code: 48, number: ""},
        marketing_email: initialLeadFormValues?.marketing_email || false,
        marketing_sms: initialLeadFormValues?.marketing_sms || false
    };

    const defaultValues = {
        ...baseDefaultValues,
        ...extraDefaultValues
    } as DefaultValues<IBaseDefaultValues & TExtraValues>;

    const gtmData: IGTMEventLeadForm = {
        clickText: leadModalState.gtmMeta.openButtonSourceText ?? "",
        source: leadModalState.gtmMeta.openButtonSource ?? "",
        vendorName: (applicationData?.vendor?.detail?.name || applicationData?.vendor?.detail?.id.toString()) ?? "",
        offerName: (applicationData?.offer?.detail?.name || offerId?.toString()) ?? ""
    };

    const resolvedExtraSchema = extraSchema
        ? extraSchema instanceof ObjectSchema
            ? extraSchema
            : object().shape(extraSchema as Record<string, Schema>)
        : null;
    const schema = resolvedExtraSchema ? baseFormSchema.concat(resolvedExtraSchema as ObjectSchema<Record<string, unknown>>) : baseFormSchema;

    const formMethods = useForm<IBaseDefaultValues & TExtraValues>({
        defaultValues: defaultValues,
        resolver: yupResolver(schema) as never
    });

    const sendLead = useSendMainLead();
    const {putPreferences} = useHomeMatchPreferencesStorage();

    const sourceSection = customSourceSection ?? leadModalState.sourceSection;

    const onInteraction = (formFillAction: "autofill" | "manual_fill" = "manual_fill") => {
        if (!wasInteractionHitRef.current && mainLeadType && sourceSection && applicationData) {
            wasInteractionHitRef.current = true;

            gtmCheckout(applicationData, viewType, mainLeadType);
            hitGTMEcommerceBeginCheckout(applicationData, viewType, mainLeadType, sourceSection, formFillAction);
        }
    };

    // Track form opening and autofill interaction
    useEffect(() => {
        if (mainLeadType && sourceSection && applicationData) {
            gtmProductAdd(applicationData, viewType, mainLeadType);
            hitGTMEcommerceAddToCart(applicationData, viewType, mainLeadType, sourceSection);

            if (initialLeadFormValues?.name || initialLeadFormValues?.email || initialLeadFormValues?.phone?.number) {
                onInteraction("autofill");
            }
        }
    }, [mainLeadType, sourceSection, applicationData]);

    useEffect(() => {
        if (resetOnChange) {
            formMethods.reset(defaultValues);
        }
    }, resetOnChange ?? []);

    const onSubmit = async (values: IBaseDefaultValues & TExtraValues) => {
        onBeforeSubmit?.(values);

        const formValues: IApplicationFormValues = transformFormValues
            ? transformFormValues(values, defaultValues as IBaseDefaultValues & TExtraValues)
            : {
                  name: values.name,
                  email: values.email,
                  phone: values.phone,
                  marketing_email: values.marketing_email,
                  marketing_sms: values.marketing_sms
              };

        setIsSending(true);

        try {
            const application = await sendLead(formValues, handleModalClose, {
                analyticsParams: analyticsParams ?? (customSourceSection ? {customSourceSection} : undefined)
            });

            if (applicationData && applicationData.offer?.detail?.region.country === Country.POLAND) {
                putPreferences(getPreferencesFromLeadData(applicationData));
            }

            await onSuccessfulSubmit(application || null, applicationData?.offer?.detail, applicationData?.property?.detail);
        } catch (error) {
            if (isValidationError(error)) {
                const fieldErrors = error.appError.fieldErrors;

                for (const fieldName of Object.keys(fieldErrors)) {
                    const errorMessage = fieldErrors[fieldName];
                    formMethods.setError(fieldName as Path<IBaseDefaultValues & TExtraValues>, {
                        message: errorMessage.join(", ")
                    });
                }
            }
        } finally {
            setIsSending(false);
            gtmEventFormInquiry(gtmData.clickText, gtmData.source, gtmData.vendorName, gtmData.offerName);
        }
    };

    return {
        formMethods,
        isSending,
        onSubmit,
        onInteraction,
        applicationData,
        vendorName,
        offerPersonalDataProcessorName,
        rodoLinks
    };
}
